type PricedUnit = {
  id: string;
  priceKhr: number;
};

export function unitSelectionPatch(units: PricedUnit[], unitId: string) {
  const unit = units.find((candidate) => candidate.id === unitId);
  return {
    unitId,
    actualPriceKhr: unit?.priceKhr ?? 0,
  };
}

export function decomposePackagingQuantity(
  baseQuantity: number,
  units: Array<{ id: string; factor: number }>,
) {
  let remaining = baseQuantity;
  const result: Array<{ unitId: string; quantity: number }> = [];
  for (const unit of [...units].sort((left, right) => right.factor - left.factor)) {
    if (unit.factor <= 0) continue;
    const quantity = Math.floor((remaining + Number.EPSILON) / unit.factor);
    if (quantity <= 0) continue;
    result.push({ unitId: unit.id, quantity });
    remaining -= quantity * unit.factor;
  }
  return { lines: result, remainingBaseQuantity: remaining };
}

type PriceSuggestionLine = {
  productId: string;
  unitId: string;
  actualPriceKhr: number;
  priceSource?: string;
};

type PriceSuggestion = {
  productId: string;
  unitId: string;
  priceKhr: number;
  source: string;
};

type PriceSuggestionRequest = {
  requestGeneration: number;
  latestGeneration: number;
};

export function preparePriceSuggestionLine<T extends PriceSuggestionLine>(
  line: T,
  productId: string,
  unitId: string,
  maintainedPriceKhr: number,
  resetToMaintainedPrice: boolean,
): T {
  if (!resetToMaintainedPrice) {
    return { ...line, productId, unitId };
  }
  return {
    ...line,
    productId,
    unitId,
    actualPriceKhr: maintainedPriceKhr,
    priceSource: "maintained_default",
  };
}

export function applyPriceSuggestion<T extends PriceSuggestionLine>(
  line: T,
  suggestion: PriceSuggestion,
  request?: PriceSuggestionRequest,
): T {
  if (
    (request && request.requestGeneration !== request.latestGeneration) ||
    line.productId !== suggestion.productId ||
    line.unitId !== suggestion.unitId ||
    line.priceSource === "manual"
  ) {
    return line;
  }
  return {
    ...line,
    actualPriceKhr: Number(suggestion.priceKhr),
    priceSource: suggestion.source,
  };
}

type DraftSummary = {
  id: string;
  documentType: string;
  title?: string;
  payloadJson?: string;
};

type NamedParty = { id: string; name: string };

type OrderLineState = {
  originalLineId?: string | null;
  productId: string;
  unitId: string;
  quantity: number;
  actualPriceKhr: number;
  notes: string;
  selected?: boolean;
  groupActualAmountKhr?: number;
  priceAdjustmentReason?: string;
};

type OrderFormState = {
  documentType: string;
  documentDate: string;
  partyId: string;
  paymentMethod: string;
  accountId: string;
  paidAmountKhr: number;
  originalDocumentId: string;
  returnMode?: "partial" | "full" | "";
  lines: OrderLineState[];
};

export function orderFormFingerprint(form: OrderFormState) {
  return JSON.stringify({
    documentType: form.documentType,
    documentDate: form.documentDate,
    partyId: form.partyId,
    paymentMethod: form.paymentMethod,
    accountId: form.accountId,
    paidAmountKhr: form.paidAmountKhr,
    originalDocumentId: form.originalDocumentId,
    returnMode: form.returnMode ?? "",
    lines: form.lines.map((line) => ({
      originalLineId: line.originalLineId ?? null,
      productId: line.productId,
      unitId: line.unitId,
      quantity: line.quantity,
      actualPriceKhr: line.actualPriceKhr,
      notes: line.notes,
      selected: line.selected ?? true,
      groupActualAmountKhr: line.groupActualAmountKhr,
      priceAdjustmentReason: line.priceAdjustmentReason ?? "",
    })),
  });
}

export function draftDisplayTitle(draft: DraftSummary, parties: NamedParty[]) {
  try {
    const payload = JSON.parse(draft.payloadJson ?? "{}") as Record<string, unknown>;
    const partyKey = draft.documentType.startsWith("sale") ? "customerId" : "supplierId";
    const partyId = typeof payload[partyKey] === "string" ? payload[partyKey] : "";
    const partyName = parties.find((party) => party.id === partyId)?.name;
    const documentDate =
      typeof payload.documentDate === "string" ? payload.documentDate : draft.title?.slice(0, 10);
    if (partyName) return `${documentDate ?? ""} ${partyName}`.trim();
  } catch {
    // Fall back to the stored title for malformed legacy payloads.
  }
  return draft.title || draft.id;
}
